import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  AlertTriangle,
  ClipboardCopy,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  createTtpaPage,
  deleteTtpaPage,
  getTtpaPageById,
  listTtpaPages,
  type TtpaPageListItem,
} from "@/lib/ttpa.functions";
import { normalizeTtpaSlug } from "@/lib/ttpa-slug";
import { copyTextToClipboard, ttpaCopyToastMessage } from "@/lib/ttpa-copy";
import { TtpaCopyFallbackDialog } from "@/components/admin/TtpaCopyFallbackDialog";
import { confirmDialog } from "@/lib/confirm-dialog";
import { ttpaNeedsReview } from "@/lib/ttpa-review";


type TtpaSearch = { freigabe?: string };

export const Route = createFileRoute("/_admin/admin/ttpa/")({
  validateSearch: (search: Record<string, unknown>): TtpaSearch => ({
    freigabe: typeof search.freigabe === "string" ? search.freigabe : undefined,
  }),
  component: TtpaAdminPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function TtpaAdminPage() {
  // Ticket AD7: Sprungziel aus dem Arbeitsvorrat – nur Seiten, die auf eine
  // Entscheidung warten.
  const { freigabe } = Route.useSearch();
  const onlyReview = freigabe === "offen";
  const listFn = useServerFn(listTtpaPages);
  const deleteFn = useServerFn(deleteTtpaPage);
  const getPageFn = useServerFn(getTtpaPageById);
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  // Ticket T6: Markdown wird erst beim Klick geladen (rein lesend).
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [fallbackText, setFallbackText] = useState<string | null>(null);


  const { data: allPages, isLoading, error } = useQuery({
    queryKey: ["admin-ttpa-pages"],
    queryFn: () => listFn(),
  });

  const pages = onlyReview ? (allPages ?? []).filter(ttpaNeedsReview) : allPages;

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-ttpa-pages"] }),
  });

  /**
   * Ticket T6: Kopiert den aktuellen Arbeitsstand (Entwurf, falls nicht
   * freigegebene Änderungen bestehen – sonst den freigegebenen Stand),
   * identisch zum Button im Seiten-Editor.
   */
  async function onCopy(item: TtpaPageListItem) {
    setCopyingId(item.id);
    try {
      const page = await getPageFn({ data: { id: item.id } });
      const text = page?.content_md ?? "";
      const ok = await copyTextToClipboard(text);
      if (ok) {
        toast.success(ttpaCopyToastMessage(item.has_unpublished_changes));
      } else {
        setFallbackText(text);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setCopyingId(null);
    }
  }


  async function onDelete(item: TtpaPageListItem) {
    if (
      !(await confirmDialog({
        title: `Seite „${item.title}" löschen?`,
        description: "Die öffentliche Adresse liefert danach nichts mehr.",
      }))
    )
      return;
    try {
      await removeMutation.mutateAsync(item.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">TTPA-Seiten</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Transparenzseiten für Kunden gemäß TTPA-Verordnung. Seiten sind nur per
            Direktlink erreichbar (<code className="rounded bg-secondary px-1">/ttpa/&lt;slug&gt;</code>)
            und werden nicht in der Sitemap gelistet.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Link
          to="/admin/ttpa/vorlage"
          className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary"
        >
          <FileText className="h-4 w-4" /> Vorlage
        </Link>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
        >
          <Plus className="h-4 w-4" /> Neue Seite
        </button>
        </div>
      </header>

      {onlyReview && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-border bg-secondary/40 p-3 text-sm">
          <span className="font-semibold text-brand-petrol">
            Gefiltert: Seiten, die auf eine Freigabe warten
          </span>
          <Link
            to="/admin/ttpa"
            search={{}}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary underline"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" /> Filter aufheben
          </Link>
        </div>
      )}

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
        {isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : !pages || pages.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {onlyReview
              ? "Keine Seite wartet aktuell auf eine Freigabe."
              : "Noch keine TTPA-Seiten angelegt."}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {pages.map((p) => (
              <li key={p.id} className="flex items-start gap-4 p-4">
                <div className="flex-1">
                  <p className="font-display text-base font-extrabold text-primary">
                    {p.title || (
                      <span className="text-muted-foreground">(ohne Titel)</span>
                    )}
                  </p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    {p.status === "published" && p.published_slug
                      ? `/ttpa/${p.published_slug}`
                      : `Entwurfs-Slug: ${p.slug}`}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span
                      className={
                        "rounded px-2 py-0.5 " +
                        (p.status === "published"
                          ? "bg-brand-wahlen/20 text-foreground"
                          : "bg-muted text-muted-foreground")
                      }
                    >
                      {p.status === "published" ? "Veröffentlicht" : "Entwurf"}
                    </span>
                    {p.status === "published" && p.has_unpublished_changes && (
                      <span className="rounded bg-brand-accent/15 px-2 py-0.5 text-foreground">
                        Nicht freigegebene Änderungen
                      </span>
                    )}
                    {p.client_name && (
                      <span className="rounded bg-secondary px-2 py-0.5 text-foreground">
                        {p.client_name}
                      </span>
                    )}
                    {p.is_orphaned && (
                      <span className="inline-flex items-center gap-1 rounded border border-destructive/30 bg-destructive/10 px-2 py-0.5 font-display font-extrabold text-destructive">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Kundenzuordnung entfernt, Entscheidung erforderlich
                      </span>
                    )}
                    <span className="text-muted-foreground">
                      Aktualisiert {dateFmt.format(new Date(p.updated_at))}
                    </span>
                  </div>
                  {p.is_orphaned && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Gelöschter Kunde:{" "}
                      {p.client_unassigned_client_name ?? "unbekannt"}
                      {p.client_unassigned_at
                        ? ` · Zuordnung entfallen am ${dateFmt.format(new Date(p.client_unassigned_at))}`
                        : ""}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {p.status === "published" && p.published_slug && (
                    <a
                      href={`/ttpa/${p.published_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Ansehen
                    </a>
                  )}

                  <button
                    onClick={() => onCopy(p)}
                    disabled={copyingId === p.id}
                    title="Markdown des aktuellen Arbeitsstands kopieren"
                    className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
                  >
                    {copyingId === p.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ClipboardCopy className="h-3.5 w-3.5" />
                    )}
                    Kopieren
                  </button>


                  <Link
                    to="/admin/ttpa/$id"
                    params={{ id: p.id }}
                    className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                  </Link>
                  <button
                    onClick={() => onDelete(p)}
                    aria-label="Löschen"
                    className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && <CreateDialog onClose={() => setCreating(false)} />}
      {fallbackText != null && (
        <TtpaCopyFallbackDialog text={fallbackText} onClose={() => setFallbackText(null)} />
      )}

    </div>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const createFn = useServerFn(createTtpaPage);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    setSaving(true);
    try {
      const finalSlug = slug.trim() || normalizeTtpaSlug(title);
      const result = await createFn({ data: { title: title.trim(), slug: finalSlug } });
      qc.invalidateQueries({ queryKey: ["admin-ttpa-pages"] });
      navigate({ to: "/admin/ttpa/$id", params: { id: result.id } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">Neue TTPA-Seite</h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Titel
            </span>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slugTouched) setSlug(normalizeTtpaSlug(e.target.value));
              }}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              placeholder="z. B. Transparenzhinweise Kunde XYZ"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Slug (URL)
            </span>
            <input
              value={slug}
              onChange={(e) => {
                setSlug(normalizeTtpaSlug(e.target.value));
                setSlugTouched(true);
              }}
              className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
              placeholder="transparenzhinweise-xyz"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Öffentlich unter <code>/ttpa/{slug || "…"}</code>
            </p>
          </label>
        </div>
        {err && (
          <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {err}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={saving || !title.trim() || !slug.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Anlegen und bearbeiten
          </button>
        </div>
      </div>
    </div>
  );
}
